import { formatCurrency,formatNumber,getCurrencySymbol } from '@angular/common';
import { FormArray, FormControl, FormGroup, Validators, ɵFormGroupValue } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import Big from 'big.js';
import { Moment } from 'moment/moment';
import { combineLatest,firstValueFrom,merge,Observable,of,switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { AccountsReceivableLedgerTemplateEntity } from '../../../dave-data-module/entities/accounts-receivable-ledger-template.entity';
import {
    AccountsReceivableLedgerEntity,
    AccountsReceivableLedgerTypeEnum, AccountsReceivableLedgerTypePreventGlobalPreCharge,
    CalcAccountsReceivableLedgerAmount,
    CalcAccountsReceivableLedgerBruttoAmountWithAllowances,
    CalcAccountsReceivableLedgerNettoAmountWithAllowances,
    CalcDiscount, financialMultiplication,
    financialRound,
    isBookingPositionTypeEnum,
    QUANTITY_MAX_DECIMAL_PLACES,
} from '../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { JobSpecificationEntity } from '../../../dave-data-module/entities/job-specification.entity';
import { LedgerImportARLCalculationPreChargeByType,LedgerImportBreakPageModeEnum,LedgerImportEntity,LedgerImportInvoiceItem,LedgerImportStatusEnum } from '../../../dave-data-module/entities/ledger-import.entity';
import { MaterialEntity } from '../../../dave-data-module/entities/material.entity';
import { QuantityTypeEntity } from '../../../dave-data-module/entities/quantity-type.entity';
import { ResourceEntity } from '../../../dave-data-module/entities/resource-dispo/resource.entity';
import { AccountsReceivableLedgerPostType,DaveMutationChangeLedgerImportArgs } from '../../../dave-data-module/graphql-types';
import { FrontendDate } from '../../../dave-data-module/helper/backend-frontend-conversion.helper';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { State } from '../../../dave-data-module/State';
import { getJobSpecificationById,getJobSpecificationFetched } from '../../../dave-data-module/State/selectors/job-specification.selector';
import { getResourceById,getResourcesFetched } from '../../../dave-data-module/State/selectors/resource-dispo/resource.selectors';
import { indexTrim,isNotNullOrUndefined,uniqArray } from '../../../helper/helper';
import { ArlCalculationSurcharge } from './arl-calculation-surcharge';
import { arlTableDataArlForm } from './arl-form-data.service';
import { calculationArlForm,calculationArlFormGroup } from './calculation-arl-form-data.service';
import { calculationArlTemplateForm } from './calculation-arl-template-form-data.service';

export const invoiceEditorAutoScrollSpeed = 18;

export function assignPositionStrings<T, K>(
    td: Array<T | K>,
    positionKey: string,
    determineIfIsArl: (toBeDetermined: T | K) => toBeDetermined is T,
    isSelected: (e: K | T) => boolean,
    isGroupEnd: (e: K) => boolean,
    isGroupStart: (e: K) => boolean,
) {
    const positions = calcPositionStringsLegacy(td, determineIfIsArl, isSelected, isGroupEnd, isGroupStart);
    return td.map((e, i) => Object.assign(e, { [positionKey]: positions[i] }));
}
export function formatPositionString(position: string) {
    const paddedParts = position.split('.');
    // remove last empty part if position has tailing dot
    if (paddedParts.length > 0 && paddedParts[paddedParts.length - 1] === '') {
        paddedParts.pop();
    }
    //    const entityParts = (v.entity.ItemNumber ?? '').split('.');
    for (let j = 0; j < paddedParts.length; j++) {
        const numStr = paddedParts[j];
        const posNum = parseInt(numStr, 10);
        if (!isNaN(posNum)) {
            // add leading Zeros
            paddedParts[j] = posNum.toString().padStart(j > 1 ? 3 : 2, '0');
        }
    }
    return paddedParts.join('.') + '.';
}
export function calcPositionStrings<T, K>(
    td: Array<T | K>,
    determineIfIsArl: (toBeDetermined: T | K) => toBeDetermined is T,
    isGroupEnd: (e: K) => boolean,
    isGroupStart: (e: T) => boolean,
    getItemNumber: (v: T | K) => string | null,
    positionsAllreadyCalculated: boolean,
    assignToEmptyItemNumbers: boolean = false,
) {
    let currPosition = 0;
    let positionPrefix = '';
    let lastposition = '00'; // muss die richtige anzahl an stellen haben
    // let lastWasGroupStart: boolean;
    // let lastWasGroupEnd: boolean;
    // let groupStack = 1;
    const usedPositions = new Set<string>();

    const incrementPosition = (value: string): string => {
        const parts = value.split('.').filter((p) => p !== '');
        const last = parts.pop();
        const lastNum = parseInt(last, 10);
        const nextNum = (Number.isNaN(lastNum) ? 0 : lastNum) + 1;
        parts.push(nextNum.toString().padStart(last.length, '0'));
        return parts.join('.') + '.';
    };

    const getNextFreePosition = (fullCandidate: string): string => {
        let candidate = fullCandidate;
        while (usedPositions.has(candidate)) {
            candidate = incrementPosition(candidate);
        }
        return candidate;
    };

    return td.map((e) => {
        const _itemNumber = getItemNumber(e);
        let position = '';
        if (!determineIfIsArl(e) && isGroupEnd(e)) {
            const prefarr = positionPrefix.split('.').filter((s) => s);
            const lastPart = prefarr.pop();
            currPosition = +lastPart;
            positionPrefix = prefarr.length ? prefarr.join('.') + '.' : '';
            lastposition = currPosition.toString().padStart(lastPart.length, '0');
            // lastWasGroupEnd = true;
            // groupStack++;
        } else {
            // Prefix, den die eingefrorene Nummer selbst impliziert (alles außer dem letzten Segment).
            // Gedachte . für Positionen die ohne . enden
            const frozenParentPrefix = (n: string) => {
                const parts = addTailingDot(n).split('.');
                parts.pop();
                parts.pop();
                return parts.length ? parts.join('.') + '.' : '';
            };
            const automaticRecalculate = _itemNumber !== null && _itemNumber !== ' ' && _itemNumber !== '' && positionsAllreadyCalculated === false;
            if (automaticRecalculate) {
                position = _itemNumber;
                if (determineIfIsArl(e) && isGroupStart(e)) {
                    // Kinder dieser Gruppe sollen mit "itemNumber" als Prefix starten (z.B. "04." -> "04.01.")
                    positionPrefix = addTailingDot(_itemNumber);
                    // lastWasGroupStart = true;
                    // groupStack++;
                    currPosition = 0;
                    lastposition = '0';
                } else {
                    // normales Element: Prefix für das NÄCHSTE Geschwister-Element berechnen
                    positionPrefix = frozenParentPrefix(_itemNumber);
                    lastposition = position;
                }
                // lastWasGroupEnd = false;
                usedPositions.add(_itemNumber);
                return _itemNumber;
            } else if (!(_itemNumber !== null || assignToEmptyItemNumbers) && !positionsAllreadyCalculated){
                return null;
            }
            if (_itemNumber !== null || assignToEmptyItemNumbers) {
                currPosition = lastSegmentNumber(lastposition);
                const rawPosition = positionPrefix + (++currPosition).toString().padStart(getLatSegment(lastposition).length, '0') + ".";
                position = getNextFreePosition(rawPosition);
                usedPositions.add(position);
                lastposition = position;
            }
            if (determineIfIsArl(e) && isGroupStart(e)) {
                positionPrefix = addTailingDot(position);
                currPosition = 0;
                lastposition = positionPrefix.split('.').length > 2 ? '000' : '00';
            }
            // lastWasGroupEnd = false;
        }


        return position;
    });
}
function addTailingDot(input: string) {
    return input.endsWith('.') ? input : input + '.';;
}
function getLatSegment(value: string) {
    const parts = value.split('.').filter((p) => p !== '');
    return parts[parts.length - 1];
}
const lastSegmentNumber = (value: string): number => {
    const parsed = parseInt(getLatSegment(value), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
};
export function calcPositionStrings2<T>(td: Array<T>, isLastFromGroup: (e: T) => boolean, getItemNumber: (v: T) => string | null, isGroupStart: (e: T) => boolean) {
    let currPosition = 0;
    let positionPrefix = '';
    // let groupStack = 1;
    let lastWasGroupEnd = false;
    return td.map((e, index) => {
        const _itemNumber = getItemNumber(e);
        // console.log(`\n[Item ${index}] ----------------------------`);
        // console.log("Item:", e);
        // console.log("Item Number:", itemNumber);

        if (_itemNumber !== ' ' && _itemNumber !== '') {
            //   console.log("→ Already has item number, returning it.");
            return _itemNumber;
        }

        let position = positionPrefix + ++currPosition;
        // console.log("Generated Position (before formatting):", position, positionPrefix,currPosition );
        // if(isGroupStart(e)){
        //     console.log("Would be groupstart")
        // }
        // if(isLastFromGroup(e)){
        //     console.log("Would be groupEnd")
        // }
        if (isGroupStart(e) && !lastWasGroupEnd) {
            // console.log("→ This item is the START of a new group.");
            // console.log("GroupStack before increment:", groupStack);

            // if (groupStack < 0) {
            //     console.log("GroupStack was negative, correcting position.");
            //     position = (parseInt(position) + 1).toString();
            // }

            positionPrefix = position + '.';
            //   console.log("New positionPrefix:", positionPrefix);

            currPosition = 0;

            // groupStack++;
            // console.log("GroupStack after increment:", groupStack);
            if (isLastFromGroup(e)) {
                //   console.log("ALSO LAST")
                lastWasGroupEnd = true;
            }
        } else if (isLastFromGroup(e) || lastWasGroupEnd) {
            // console.log("→ This item is the LAST in its group.");
            const prefarr = positionPrefix.split('.').filter((s) => s);
            let first = parseInt(prefarr[0] || '0', 10);
            // console.log("Current Prefix Array:", prefarr);
            // console.log("Resetting positionPrefix and currPosition to:", first);
            // groupStack--;
            if (prefarr.length !== 0) {
                position = (first + 1).toString();
            }
            if (!isGroupStart(e)) {
                positionPrefix = '';
            } else {
                currPosition = 0;
                positionPrefix =
                    position
                        .split('.')
                        .map((part) => part.padStart(2, '0'))
                        .join('.') + '.';
            }

            if (isLastFromGroup(e)) {
                //  console.log("LAST")
                lastWasGroupEnd = true;
            } else {
                lastWasGroupEnd = false;
            }
        }
        // const formattedPosition = position
        //     .split('.')
        //     .map((part) => part.padStart(2, '0'))
        //     .join('.');
        //
        // // console.log("Final Position:", formattedPosition);
        // // console.log("GroupStack:", groupStack);
        // // console.log("CurrPosition:", currPosition);
        //
        // return formattedPosition + '.';
        return formatPositionString(position);
    });
}

export function calcPositionStringsLegacy<T, K>(td: Array<T | K>, determineIfIsArl: (toBeDetermined: T | K) => toBeDetermined is T, isSelected: (e: K | T) => boolean, isGroupEnd: (e: K) => boolean, isGroupStart: (e: K) => boolean) {
    let currPosition = 0;
    let positionPrefix = '';
    let groupSum = 0;
    return td.map((e) => {
        let position = '';

        if (determineIfIsArl(e)) {
            // let position = '';

            if (isSelected(e)) {
                position = positionPrefix + ++currPosition;
                // groupSum += e.entity.CompleteCost;
            }
        } else {
            // let position = '';
            if (isSelected(e)) {
                if (isGroupEnd(e)) {
                    const prefarr = positionPrefix.split('.').filter((s) => s);
                    currPosition = +prefarr.pop();
                    positionPrefix = prefarr.length ? prefarr.join('.') + '.' : '';
                    // Object.assign(e, { amount: groupSum });
                } else {
                    position = positionPrefix + ++currPosition;

                    if (isGroupStart(e)) {
                        positionPrefix = /*positionPrefix +*/ position + '.';
                        currPosition = 0;
                        groupSum = 0;
                    }
                }
            }
        }
        return position;
    });
}
// interface bpi {
//     BookingText: string;
//     Information: string;
//     Quantity: number;
//     QuantityTypeId: number;
//     BaseCost: number;
//     CurrencyCode: string;
//     CompleteCost: number;
//     CompleteTaxCost: number;
//     Type: AccountsReceivableLedgerTypeEnum;
//     Date: Date;
//     IsVisible: boolean;
//     ArlIds: number[];
//     Id: number;
//     Tax: number;
//
//     Longtext?: string;
//     ShowLongtext?: boolean;
//     AlreadyPayed?: number;
// }
type bpi = Pick<
    AccountsReceivableLedgerEntity,
    | 'SalaryBaseCost'
    | 'BookingText'
    | 'Information'
    | 'Quantity'
    | 'QuantityTypeId'
    | 'BaseCost'
    | 'BaseCostGross'
    | 'CurrencyCode'
    | 'CompleteCost'
    | 'CompleteTaxCost'
    | 'Type'
    | 'Date'
    | 'IsVisible'
    | 'ArlIds'
    | 'Id'
    | 'Tax'
    | 'Longtext'
    | 'ShowLongtext'
    | 'AlreadyPayed'
    | 'IsOptional'
    | 'CrossedOut'
    | 'ItemNumber'
    | 'HideInGroupSummary'
    | 'StartOnNewPage'
    | 'ParentId'
    | 'BaseCostAllowances'
    | 'CostAmountAllowance'
    | 'ShowQuantityHistory'
    | 'QuantityHistory'
    | 'QuantityFormula'
    | 'Gap'
    | 'SurchargeOverride'
    | 'FixedPrices'
>;

type arlFromFormType = {
    InheritFromChildren: boolean;
    Type: AccountsReceivableLedgerTypeEnum;
    Quantity: number;
    ARLIds: number[];
    BaseCost: number;
    SalaryBaseCost: number;
    getBaseCost?: () => number;
    IsVisible?: boolean;
    Longtext?: string;
    ShowLongtext?: boolean;
    BookingText: string;
    Information: string;
    QuantityTypeId: number;
    IsOptional: boolean;
    CrossedOut: boolean;
    ParentId?: number | null; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    Date?: Moment; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    Tax?: number; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    AlreadyPayed?: number; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    ItemNumber?: string;
    HideInGroupSummary?: boolean;
    StartOnNewPage?: boolean; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    BaseCostAllowances: AccountsReceivableLedgerEntity['BaseCostAllowances']; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    Id?: number; // bei den formControls aus useArlForms enthalten bei calculationFormMap nicht immer
    isCalculationArl: boolean;
    CostAmountAllowance: number;
    BaseCostGross: number;
    ShowQuantityHistory?: boolean; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    QuantityHistory?: AccountsReceivableLedgerEntity['QuantityHistory']; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    QuantityFormula: string; // nur bei den formControls aus useArlForms enthalten bei calculationFormMap (noch) nicht
    Gap?: AccountsReceivableLedgerEntity['Gap'];
    SurchargeOverride: number | null;
    FixedPrices: boolean;
};
export const getParents = <T extends {ParentId: number}>(id: number, arlDict: Dictionary<T>) => {
    const parents: T[] = [];
    let pid = arlDict[id]?.ParentId;
    while (pid) {
        const p = arlDict[pid];
        parents.push(p);
        pid = p.ParentId;
    }
    return parents;
};

function wrapLongWords(i: string | null) {
    if (!i) return i;
    return i.split(' ')
        .map((x:string)=>x.length>16?'<span class="fix_long_word">'+x+'</span>':x)
        .join(' ')
}

export const getBookingPositionTwigVariables = (
    hidePositionsWithoutQuantity: boolean,
    arlIds: Array<number> = [],
    invoiceItems: Array<LedgerImportInvoiceItem> = [],
    quantityTypes: QuantityTypeEntity[],
    allowanceCharges: { percentage: number; amount: number; usePercentage: boolean; customReason: string }[],
    arlDict: Dictionary<AccountsReceivableLedgerEntity>,
    skontoPercentage: number,
    breakPageMode: LedgerImportBreakPageModeEnum,
    defaultCurrencyCode: string,
    ledgerImportShowLongInformation: boolean,
    calculateBruttoSumAndTaxValuesFromNettoSum: boolean,
    ignoreCalculationArls: boolean,
    ARLCalculationGlobalPreCharge: number,
    ARLCalculationPreChargeByTypes: { percentage: number | null; arlType: AccountsReceivableLedgerTypeEnum }[],
    useArlForms?: (id: number) => arlTableDataArlForm,
    calculationFormMap?: Map<number, Array<ɵFormGroupValue<calculationArlForm> & { getBaseCost: () => number }>>,
    defaultValuesForAddedArls?: { CurrencyCode: string; IsVisible: boolean; Tax: number },
) => {
    // todo: arlIdsFromLedgerImports einbauen
    console.group('getBookingPositionTwigVariables');
    const _getParentId = (id: number) => {
        return (useArlForms ? useArlForms(id)?.value : arlDict[id])?.ParentId;
    }
    const _getAtl = (id: number): Pick<AccountsReceivableLedgerEntity, 'Quantity' | 'BaseCost' | 'Tax' | 'Id' | 'BaseCostAllowances' | 'ParentId'>=> {
        // todo geht kaputt wenn man eine neue position in der kalkulation hat und versucht diese im pdf an zu zeigen
        if (useArlForms) {
            const form = useArlForms(id);
            return {
                Quantity: form.controls.Quantity.getRawValue(),
                BaseCost: form.controls.BaseCost.getRawValue(),
                Tax: form.controls.Tax.getRawValue(),
                Id: form.controls.Id.getRawValue(),
                BaseCostAllowances: form.controls.BaseCostAllowances.getRawValue(),
                ParentId: form.controls.ParentId.getRawValue(),
            }
        } else {
            return arlDict[id];
        }
    }
    const getParents = (id: number) => {
        const parents: Pick<AccountsReceivableLedgerEntity, 'Quantity' | 'BaseCost' | 'Tax' | 'Id' | 'BaseCostAllowances' | 'ParentId'>[] = [];
        let pid = _getParentId(id);
        while (pid) {
            const p = _getAtl(pid);
            parents.push(p);
            pid = p.ParentId;
        }
        return parents;
    };

    const getParentBaseCostAllowances = (id: number) => {
        return getParents(id).map((p) => p.BaseCostAllowances);
    };

    const getArlFormDataWithChildren = (
        id: number,
        position: string,
        parentId: number,
        index: number,
        quantityMultiplier: number = 1,
        inheritFromChildren: boolean = false,
    ): Array<{ values: bpi; position: string; quantityMultiplier: number; onlyForCalculation: boolean; isGroupEnd?: boolean; isCalculationArl: boolean }> => {
        const arlFromState = arlDict[id];
        if (id && !arlFromState) {
            //arl must be deleted
            return [];
        }
        // console.log({ arlFromState, quantityMultiplier });
        const returnFromEntity = () => {
            if (!arlFromState) {
                console.error('arl not found, id; ' + id);
                return [];
            }

            const nettoAmount = CalcAccountsReceivableLedgerNettoAmountWithAllowances(arlFromState, getParentBaseCostAllowances(arlFromState.Id));
            const bruttoAmount = CalcAccountsReceivableLedgerBruttoAmountWithAllowances(arlFromState, getParentBaseCostAllowances(arlFromState.Id));
            const taxAmount = financialRound(bruttoAmount - nettoAmount);
            const ret: ReturnType<typeof getArlFormDataWithChildren> = [
                {
                    values: {
                        ...arlFromState,
                        CompleteCost: bruttoAmount,
                        CompleteTaxCost: taxAmount,
                    },
                    position,
                    quantityMultiplier,
                    onlyForCalculation: inheritFromChildren,
                    isCalculationArl: false,
                },
                ...((arlFromState.InheritFromChildren && !ignoreCalculationArls) || arlFromState.Type === AccountsReceivableLedgerTypeEnum.Group ? arlFromState.ArlIds : [])
                    .map((id, i) => getArlFormDataWithChildren(id, position + '.' + (i + 1), arlFromState.Id, i, arlFromState.Type === AccountsReceivableLedgerTypeEnum.Group ? 1 : arlFromState.Quantity, arlFromState.InheritFromChildren))
                    .flat()
                    .map((a) => {
                        const isCalculationArl = a.values.ParentId === arlFromState.Id ? arlFromState.Type !== AccountsReceivableLedgerTypeEnum.Group : a.isCalculationArl;
                        const nettoAmount = CalcAccountsReceivableLedgerNettoAmountWithAllowances(a.values, isCalculationArl ? [] : getParentBaseCostAllowances(a.values.Id));
                        const bruttoAmount = CalcAccountsReceivableLedgerBruttoAmountWithAllowances(a.values, isCalculationArl ? [] : getParentBaseCostAllowances(a.values.Id));
                        const taxAmount = financialRound(bruttoAmount - nettoAmount);
                        return {
                            ...a,
                            values: {
                                ...a.values,
                                CompleteCost: bruttoAmount,
                                CompleteTaxCost: taxAmount,
                            },
                            isCalculationArl,
                        };
                    }),
            ];
            if (arlFromState.Type === AccountsReceivableLedgerTypeEnum.Group) {
                ret.push({
                    values: arlFromState,
                    position,
                    quantityMultiplier,
                    onlyForCalculation: false,
                    isCalculationArl: false,
                    isGroupEnd: true,
                });
            }
            return ret;
        };
        if (!useArlForms) {
            return returnFromEntity();
        }
        const form = useArlForms(id);
        let arlFromForm: arlFromFormType = form ? { CostAmountAllowance: 0, ...form.getRawValue(), isCalculationArl: false, QuantityHistory: form.getRawValue().QuantityHistory?.map((q) => ({ ...q, date: q.date?.toDate() })), Gap: form.getRawValue().Gaps?.map((g) => ({ ...g })) } : undefined;
        if (!arlFromForm) {
            // todo das ist für die arls in der Kalkulation, deren forms stehen (noch) nicht in dem formservice
            if (!ignoreCalculationArls && calculationFormMap?.has(parentId)) {
                const temp = calculationFormMap.get(parentId)[index];
                if (isNotNullOrUndefined(temp?.getBaseCost)) {
                    arlFromForm = {
                        InheritFromChildren: undefined,
                        ARLIds: undefined,
                        BaseCost: undefined,
                        BaseCostGross: temp.BaseCost,
                        SalaryBaseCost: temp.Type === AccountsReceivableLedgerTypeEnum.EmployeeArl ? temp.getBaseCost() : 0,
                        Type: temp.Type,
                        Quantity: temp.Quantity,
                        QuantityTypeId: temp.QuantityTypeId,
                        IsOptional: temp.IsOptional,
                        CrossedOut: temp.CrossedOut,
                        HideInGroupSummary: temp.HideInGroupSummary,
                        StartOnNewPage: false,
                        Date: null,
                        Tax: null,
                        AlreadyPayed: null,
                        BaseCostAllowances: [],
                        Id: temp.Id,
                        ...temp,
                        BookingText: temp.BookingText,
                        Information: temp.Information,
                        isCalculationArl: true,
                        CostAmountAllowance: temp.CostAmountAllowance,
                        QuantityHistory: null,
                        ShowQuantityHistory: false,
                        QuantityFormula: temp.QuantityFormula,
                        Gap: [],
                        SurchargeOverride: temp.SurchargeOverride,
                        FixedPrices: false,
                    };
                    arlFromForm.BaseCost = arlFromForm.getBaseCost();
                }
            }
            // console.log('arlFromForm 1', arlFromForm, arlFromState);
            if (!arlFromForm) {
                return returnFromEntity();
            }
        }
        // console.log('arlFromForm 2', arlFromForm, arlFromState);

        const arlsToReturn = [];
        let missingArls = 0;
        ((arlFromForm.InheritFromChildren && !ignoreCalculationArls) || arlFromForm.Type === AccountsReceivableLedgerTypeEnum.Group ? arlFromForm.ARLIds : [])?.forEach((childId, i) => {
            const bpsWithChildren = getArlFormDataWithChildren(childId, position + '.' + (i - missingArls + 1), id, i, arlFromForm.Type === AccountsReceivableLedgerTypeEnum.Group ? 1 : arlFromForm.Quantity, arlFromForm.InheritFromChildren);
            if (bpsWithChildren.length) {
                arlsToReturn.push(...bpsWithChildren);
            } else {
                missingArls++;
            }
        });
        const useTax = isNotNullOrUndefined(arlFromForm?.Tax) ? arlFromForm.Tax : arlFromState ? arlFromState.Tax : defaultValuesForAddedArls.Tax;

        // console.log('a', arlFromForm);
        const nettoAmount = CalcAccountsReceivableLedgerNettoAmountWithAllowances(arlFromForm, arlFromForm.isCalculationArl ? [] : getParentBaseCostAllowances(arlFromForm.Id));
        const bruttoAmount = CalcAccountsReceivableLedgerBruttoAmountWithAllowances(arlFromForm, arlFromForm.isCalculationArl ? [] : getParentBaseCostAllowances(arlFromForm.Id));
        const taxAmount = financialRound(bruttoAmount - nettoAmount);

        const values: bpi = {
            CurrencyCode: arlFromState ? arlFromState.CurrencyCode : defaultValuesForAddedArls.CurrencyCode,
            CompleteCost: bruttoAmount,
            CompleteTaxCost: taxAmount,
            IsVisible: isNotNullOrUndefined(arlFromForm?.IsVisible) ? arlFromForm.IsVisible : arlFromState ? arlFromState.IsVisible : defaultValuesForAddedArls.IsVisible,
            ArlIds: arlFromForm.ARLIds,
            Id: id,
            Tax: useTax,
            ShowLongtext: arlFromForm.ShowLongtext,
            ...arlFromForm,
            Longtext: wrapLongWords(arlFromForm.Longtext),
            Information: wrapLongWords(arlFromForm.Information),
            BookingText: wrapLongWords(arlFromForm.BookingText),
            Quantity: arlFromForm.Quantity || 0,
            AlreadyPayed: arlFromForm.AlreadyPayed,
            Date: arlFromForm.Date?.toDate(),
            ItemNumber: arlFromForm.ItemNumber,
            HideInGroupSummary: arlFromForm.HideInGroupSummary,
            StartOnNewPage: arlFromForm.StartOnNewPage,
            ParentId: arlFromForm.ParentId,
            BaseCostAllowances: arlFromForm.BaseCostAllowances,
            ShowQuantityHistory: arlFromForm.ShowQuantityHistory,
            QuantityHistory: arlFromForm.QuantityHistory,
            QuantityFormula: arlFromForm.QuantityFormula,
            BaseCostGross: arlFromForm.BaseCostGross,
            Gap: arlFromForm.Gap,
        };
        const ret: ReturnType<typeof getArlFormDataWithChildren> = [
            {
                values,
                quantityMultiplier,
                position,
                onlyForCalculation: inheritFromChildren,
                isCalculationArl: arlFromForm.isCalculationArl,
            },
            ...arlsToReturn, //arlFromForm.ARLIds.map((id, i) => getArlFormDataWithChildren(id, position + '.' + (i + 1))).flat(),
        ];
        if (arlFromForm.Type === AccountsReceivableLedgerTypeEnum.Group) {
            ret.push({
                values,
                position,
                quantityMultiplier,
                onlyForCalculation: false,
                isGroupEnd: true,
                isCalculationArl: false,
            });
        }
        return ret;
    };

    // const BookingPositions = arlIds.map(id => getArlFormData(id));
    const BookingPositions: ReturnType<typeof getArlFormDataWithChildren> = [];

    let missingArls = 0;
    arlIds.forEach((id, i) => {
        const bpsWithChildren = getArlFormDataWithChildren(id, i - missingArls + 1 + '', null, i);
        if (bpsWithChildren.length) {
            BookingPositions.push(...bpsWithChildren);
        } else {
            missingArls++;
        }
    }); //.flat();
    const filteredForSums = BookingPositions.filter((a) => !a.isCalculationArl && arlIsAmountRelevant(a.values)).map((v) => v.values);
    console.log('new BookingPositions', BookingPositions, 'filteredForSums', filteredForSums);
    console.log(filteredForSums.filter((a) => a.Id >= 167626 && a.Id <= 167640));
    const bestSums = getLedgerImportSums(filteredForSums, allowanceCharges, calculateBruttoSumAndTaxValuesFromNettoSum, 0, []);
    console.warn('######## bestSums ##########');
    console.log(bestSums);

    const SumNetto = bestSums.withoutAllowanceCharges.nettoSum;
    const Sum = bestSums.withoutAllowanceCharges.bruttoSum;
    const SumNettoWithDiscount = bestSums.withAllowanceCharges.nettoSum;
    const SumWithDiscountAsNumber = bestSums.withAllowanceCharges.bruttoSum;

    const discountPercentage = SumNetto ? 1 - SumNettoWithDiscount / SumNetto : 1;

    // hier wird einfach der currencyCode qus dem ersten arl genommen, das BE stellt sicher das es keine verschiedenen in einer Rechnung gibt
    const CurrencyCode = BookingPositions.find((bp) => true)?.values?.CurrencyCode || defaultCurrencyCode;

    const taxes: { taxType: number; taxAmount: number; taxAmountLabel: string }[] = [];
    bestSums.withAllowanceCharges.taxSumsByType.forEach((val, key) => {
        taxes.push({ taxType: key, taxAmount: val, taxAmountLabel: CurrencyCode ? formatCurrency(val, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode) : formatNumber(val, 'de-DE', '1.2-2') });
    });

    let DiscountAmountString = '';
    let SumWithDiscountString = '';
    let SumNettoString = SumNetto + '';
    let SumString = Sum + '';
    if (CurrencyCode) {
        // const CurrencyCode = BookingPositions[0].CurrencyCode;
        DiscountAmountString = formatCurrency(+(+SumNettoWithDiscount - +SumNetto).toFixed(2), 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode);

        SumNettoString = formatCurrency(SumNetto, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode);
        SumWithDiscountString = formatCurrency(+SumWithDiscountAsNumber, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode);
        SumString = formatCurrency(Sum, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode);
    }
    const digitsInfo = '1.2-2';
    const getBPVar = (isCalculationArl: boolean, b: bpi, quantityMultiplier: number, index: number, parentAllowances: AccountsReceivableLedgerEntity['BaseCostAllowances'][]) => {

        const getCorrectedBaseCostFromCalculationArl = (baseCost: number) => {
            const typePreCharge = ARLCalculationPreChargeByTypes.find((c) => c.arlType === b.Type);
            let percentFactor: number = 1;
            if (typePreCharge?.percentage) {
                percentFactor = percentFactor * (1 + typePreCharge.percentage);
            }
            if (ARLCalculationGlobalPreCharge) {
                percentFactor = percentFactor * (1 + ARLCalculationGlobalPreCharge);
            }
            return baseCost * percentFactor;
        }
        if (isCalculationArl) {

            const parent = _getAtl(b.ParentId);
            parentAllowances = [parent.BaseCostAllowances, ...getParentBaseCostAllowances(parent.Id)]

            const BaseCost = isNotNullOrUndefined(b.SurchargeOverride) ? (b.BaseCost || 0) : financialRound(getCorrectedBaseCostFromCalculationArl(b.BaseCost || 0));
            const SalaryBaseCost = isNotNullOrUndefined(b.SurchargeOverride) ? (b.SalaryBaseCost || 0) : financialRound(getCorrectedBaseCostFromCalculationArl(b.SalaryBaseCost || 0));
            const BaseCostGross = BaseCost * (1 + (parent.Tax / 100));

            // console.log('quantityMultiplier', quantityMultiplier)
            // console.log('BaseCost', BaseCost);
            // console.log('SalaryBaseCost', SalaryBaseCost);
            // console.log('parent', parent);
            // console.log('BaseCostGross', BaseCostGross);
            // console.log('CompleteCost', CompleteCost);
            // console.log('completeCostNetto', completeCostNetto);
            // console.log('CompleteTaxCost', CompleteTaxCost);
            b = {
                ...b,
                BaseCost,
                BaseCostGross,
                SalaryBaseCost,
            };


            const completeCostNetto = CalcAccountsReceivableLedgerNettoBaseCostWithAllowancesCharges(b, parentAllowances) * (b.Quantity || 0);
            const CompleteCost = completeCostNetto * (1 + (parent.Tax / 100));
            const CompleteTaxCost = CompleteCost - completeCostNetto;
            b = {
                ...b,
                CompleteCost,
                CompleteTaxCost,
            };
        }

        const baseCostWithArlCharges = CalcAccountsReceivableLedgerNettoBaseCostWithOnlyCharges(b, parentAllowances);
        const baseCostWithArlAllowanceCharges = CalcAccountsReceivableLedgerNettoBaseCostWithAllowancesCharges(b, parentAllowances);

        const baseCostWithTax = CalcAccountsReceivableLedgerBruttoBaseCostWithOnlyCharges(b, parentAllowances);
        const baseCostWithArlAllowanceChargesAndTax = CalcAccountsReceivableLedgerBruttoBaseCostWithAllowancesCharges(b, parentAllowances);

        const baseCostSalaryRatio = b.BaseCost ? b.SalaryBaseCost / b.BaseCost : 0;

        const salaryBaseCostWithArlCharges = financialRound(baseCostWithArlCharges * baseCostSalaryRatio);
        const salaryBaseCostWithArlAllowanceCharges = financialRound(baseCostWithArlAllowanceCharges * baseCostSalaryRatio);

        const salaryBaseCostWithTax = CalcAccountsReceivableLedgerAmount(1, b.Tax, salaryBaseCostWithArlCharges);

        const salaryBaseCostWithArlAllowanceChargesAndTax = CalcAccountsReceivableLedgerAmount(1, b.Tax, salaryBaseCostWithArlAllowanceCharges);
        const baseCostWithTaxUndefined = baseCostWithTax === undefined;
        if(b?.Gap && b.Gap.length > 0 && ledgerImportShowLongInformation && b.ShowLongtext){
            const sortedGaps = [...b.Gap]
                .sort((a, b) => b.indexInLongText - a.indexInLongText);

            let text = b.Longtext ?? '';

            for (const gap of sortedGaps) {
                if (!gap.caption && !gap.body) {
                    continue
                }
                const index = gap.indexInLongText;

                const gapText =
                    index !== 0
                        ? "\n" + gap.caption + " " + gap.body
                        : gap.caption + " " + gap.body;

                text =
                    text.slice(0, index) +
                    gapText +
                    text.slice(index);
            }

            b.Longtext = text;
        }

        const isBookingPositionType = isBookingPositionTypeEnum(b.Type);
        return {
            BookingText: b.BookingText || '',
            Information: b.Information || '',
            Quantity: isBookingPositionType && b.Quantity ? formatNumber(b.Quantity * quantityMultiplier, 'de-DE', digitsInfo) + '' : '',
            QuantityType: quantityTypes.find((q) => q.Id === b.QuantityTypeId)?.Name || '',

            BaseCost: isBookingPositionType ? formatNumber(baseCostWithArlCharges, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            BaseCostWithTax: isBookingPositionType && !baseCostWithTaxUndefined ? formatNumber(baseCostWithTax, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            BaseCostWithArlAllowanceCharges: isBookingPositionType ? formatNumber(baseCostWithArlAllowanceCharges, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            BaseCostWithArlAllowanceChargesAndTax: isBookingPositionType ? formatNumber(baseCostWithArlAllowanceChargesAndTax, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',

            SalaryBaseCost: isBookingPositionType ? formatNumber(salaryBaseCostWithArlCharges, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            SalaryBaseCostWithTax: isBookingPositionType ? formatNumber(salaryBaseCostWithTax, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            SalaryBaseCostWithArlAllowanceCharges: isBookingPositionType ? formatNumber(salaryBaseCostWithArlAllowanceCharges, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            SalaryBaseCostWithArlAllowanceChargesAndTax: isBookingPositionType ? formatNumber(salaryBaseCostWithArlAllowanceChargesAndTax, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',

            NotSalaryBaseCost: isBookingPositionType ? formatNumber(financialRound(baseCostWithArlCharges - salaryBaseCostWithArlCharges), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            NotSalaryBaseCostWithTax: isBookingPositionType ? formatNumber(baseCostWithTax - salaryBaseCostWithTax, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || '' : '',
            NotSalaryBaseCostWithArlAllowanceCharges: isBookingPositionType
                ? formatNumber(baseCostWithArlAllowanceCharges - salaryBaseCostWithArlAllowanceCharges, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || ''
                : '',
            NotSalaryBaseCostWithArlAllowanceChargesAndTax: isBookingPositionType
                ? formatNumber(baseCostWithArlAllowanceChargesAndTax - salaryBaseCostWithArlAllowanceChargesAndTax, 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) || ''
                : '',

            CompleteCost: isBookingPositionType ? formatNumber(+(((b.CompleteCost || 0) - (b.CompleteTaxCost || 0)) * quantityMultiplier).toFixed(4), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) : '',
            CompleteCostWithTax:
                isBookingPositionType && !baseCostWithTaxUndefined ? formatNumber(+((b.CompleteCost || 0) * quantityMultiplier).toFixed(4), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) : '',
            Longtext: ledgerImportShowLongInformation && b.ShowLongtext && b.Longtext ? b.Longtext : '',
            isBookingPosition: isBookingPositionType,

            CompleteTaxCost: isBookingPositionType ? formatNumber(+((b.CompleteTaxCost || 0) * quantityMultiplier).toFixed(4), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) : '',
            Tax: b.Tax,
            Amount: isBookingPositionType ? formatNumber(+((b.CompleteCost || 0) * quantityMultiplier).toFixed(4), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo) : '',
            AlreadyPayed: isNotNullOrUndefined(b.AlreadyPayed) ? formatNumber(b.AlreadyPayed, 'de-DE', digitsInfo) : '',
            ARLAllowances: isBookingPositionType ? b.BaseCostAllowances.filter((a) => (!a.arlType || a.arlType === b.Type) && a.percent < 0) : b.BaseCostAllowances.filter((a) => a.percent < 0),
            Date: b.Date,
            IsOptional: b.IsOptional,
            CrossedOut: b.CrossedOut,
            CurrencySymbol: getCurrencySymbol(b.CurrencyCode, 'narrow'),
            HideInGroupSummary: b?.HideInGroupSummary,
            StartOnNewPage:
                b.StartOnNewPage ||
                (index > 0 &&
                    (breakPageMode === LedgerImportBreakPageModeEnum.All ||
                        (b.Type === AccountsReceivableLedgerTypeEnum.Group && (breakPageMode === LedgerImportBreakPageModeEnum.Title || (!b.ParentId && breakPageMode === LedgerImportBreakPageModeEnum.FirstLayerTitle))))),
            isGroupStart: b.Type === AccountsReceivableLedgerTypeEnum.Group,
            QuantityHistory: b.QuantityHistory,
            ShowQuantityHistory: b.ShowQuantityHistory,
            isRoot: arlIds.includes(b.Id),
        };
    };
    const bps: Array<ReturnType<typeof getBPVar>> = [];

    BookingPositions.forEach((b, index) => {
        if (b.values.IsVisible /*|| !b.values.ArlIds || !b.values.ArlIds.length*/) {
            // const arl = getArlFormData(b.Id);
            // if (!arl) {
            //     throw new Error('ARL not found');
            // }

            if (hidePositionsWithoutQuantity && (!b.values.Quantity) && isBookingPositionTypeEnum(b.values.Type)) {
                return;
            }

            let vars;
            if (b.isGroupEnd) {
                // console.warn('# group end');
                // console.log(vars);
                // console.log(b);
                const nettoSums: number[] = [];
                const bruttoSums: number[] = [];
                const baseCostWithArlCharges: number[] = [];
                // const baseCostWithArlAllowanceCharges: number[] = [];

                for (let i = index - 1; i > 0; i--) {
                    // console.log({ i, index });
                    const v = BookingPositions[i];
                    if (v.values.Id === b.values.Id) {
                        i = 0;
                    } else if (v.values.IsVisible) {
                        if (!v.onlyForCalculation && !v.values.IsOptional && !v.values.CrossedOut) {
                            nettoSums.push((v.values.CompleteCost || 0) - (v.values.CompleteTaxCost || 0));
                            bruttoSums.push(v.values.CompleteCost || 0);
                            const parents = getParents(v.values.Id);
                            const myIndex = parents
                                .slice()
                                .reverse()
                                .findIndex((p) => p.Id === b.values.Id);
                            const parentBCAsWOAllowances = parents
                                .slice()
                                .reverse()
                                .map((p, index) => p.BaseCostAllowances.filter((c) => index > myIndex || c.percent > 0))
                                .reverse();

                            baseCostWithArlCharges.push(CalcAccountsReceivableLedgerNettoAmountWithAllowances(v.values, parentBCAsWOAllowances, false));
                        }
                    }
                }
                vars = {
                    Information: `Summe ${b.values.ItemNumber}: ${b.values.Information || ''}`,
                    CompleteCost: b.values.IsOptional
                        ? 'optional'
                        : formatNumber(+(nettoSums.reduce((p, c) => p + c, 0) * b.quantityMultiplier).toFixed(4), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo),
                    CompleteCostWithTax: b.values.IsOptional
                        ? 'optional'
                        : formatNumber(+(bruttoSums.reduce((p, c) => p + c, 0) * b.quantityMultiplier).toFixed(4), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo),
                    BaseCost: formatNumber(financialRound(baseCostWithArlCharges.reduce((p, c) => p + c, 0) * b.quantityMultiplier), 'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/, digitsInfo),
                    // BaseCostWithArlAllowanceCharges: formatNumber(
                    //     financialRound(baseCostWithArlAllowanceCharges.reduce((p, c) => p + c, 0) * b.quantityMultiplier),
                    //     'de-DE' /*getCurrencySymbol(b.CurrencyCode, 'narrow'), b.CurrencyCode*/,
                    //     digitsInfo,
                    // ),

                    isBookingPosition: false,
                    isGroupEnd: true,
                    Position: '',
                    CurrencySymbol: getCurrencySymbol(b.values.CurrencyCode, 'narrow'),
                    ARLAllowances: b.values.BaseCostAllowances.filter((a) => a.percent < 0),
                };
            } else {
                vars = getBPVar(b.isCalculationArl, b.values, b.quantityMultiplier, index, b.isCalculationArl ? [] : getParentBaseCostAllowances(b.values.Id));
            }

            bps.push({
                Position: b.values.ItemNumber != null ? b.values.ItemNumber : '',
                isCalculationArl: !!b.onlyForCalculation,
                depth: indexTrim(b.values.ItemNumber || '', '.').split('.').length,
                ...vars,
            });
        }
    });
    console.log({ bps, BookingPositions });
    console.groupEnd();
    const AllowanceCharges = allowanceCharges.map((ac, index) => {
        return {
            customReason: ac.customReason,
            usePercentage: ac.usePercentage,
            percentage: formatNumber((ac.percentage || 0) * 100, 'de-DE', '1.0-2'),
            amount: formatCurrency(ac.amount || 0, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode),
            sum: formatCurrency(
                addAllowanceCharges(
                    SumNetto,
                    allowanceCharges.filter((a, i) => i <= index),
                ) || 0,
                'de-DE',
                getCurrencySymbol(CurrencyCode, 'narrow'),
                CurrencyCode,
            ),
        };
    });
    let SkontoAllowance: { percentage: string; amount: string; sum: string; usePercentage: boolean; customReason: string } = null;
    if (skontoPercentage) {
        const sum = addAllowanceCharges(SumWithDiscountAsNumber, [], skontoPercentage) || 0;
        const amount = sum - SumWithDiscountAsNumber || 0;
        SkontoAllowance = {
            customReason: 'Skonto',
            usePercentage: true,
            percentage: formatNumber(skontoPercentage * -1, 'de-DE', '1.0-2'),
            // amount: formatCurrency(ac.amount, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode),
            amount: formatCurrency(amount, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode),
            sum: formatCurrency(sum, 'de-DE', getCurrencySymbol(CurrencyCode, 'narrow'), CurrencyCode),
        };
    }
    return {
        SumNetto: SumNettoString,
        SumNettoNumber: SumNetto,
        SumNettoWithDiscount,
        Sum: SumString,
        SumWithDiscount: SumWithDiscountString,
        SkontoAllowance,
        Taxes: taxes.filter((t) => t.taxAmount).map((t) => ({ tax: t.taxType, sum: t.taxAmountLabel })),
        DiscountAmount: DiscountAmountString,
        DiscountPercentage: financialRound(discountPercentage * 100).toString(),
        BookingPositions: bps,
        AllowanceCharges,
        DefaultCurrencySymbol: getCurrencySymbol(CurrencyCode, 'narrow'),
    };
};

export function _getArlChildren<T extends { Type: AccountsReceivableLedgerTypeEnum; ArlIds?: number[] }>(id: number, allArls: Dictionary<T>, excludeCalculationArls = true): T[] {
    const arl = allArls[id];
    let ret: T[] = null;
    if (!arl) {
        console.error('arl not found, id; ' + id);
        return [];
    }
    if ((!excludeCalculationArls || arl.Type === AccountsReceivableLedgerTypeEnum.Group) && arl.ArlIds?.length) {
        ret = [
            arl,
            ...uniqArray(arl.ArlIds)
                .map((i) => _getArlChildren(i, allArls))
                .flat(),
        ];
    } else {
        ret = [arl];
    }
    return ret;
}
// const getChildren = (bpId: number, submittedBookingPositions: AccountsReceivableLedgerEntity[]): AccountsReceivableLedgerEntity[] => {
//     const bp = submittedBookingPositions.find(b => b.Id === bpId);
//     if (bp?.ArlIds.length && bp.Type === ARLTypeEnum.Group) {
//         return [bp, ...bp.ArlIds.map(id => getChildren(id, submittedBookingPositions)).flat()]
//     } else if (bp) {
//         return [bp];
//     }
//     return []
// }
export function calculateConsolidatedAmount(
    arlDict: Dictionary<AccountsReceivableLedgerEntity>,
    // arlDict: Dictionary<{ Type: AccountsReceivableLedgerTypeEnum; ArlIds?: number[]; CompleteCost: number; IsOptional: boolean; CrossedOut: boolean }>,
    arlIds: number[],
    allowanceCharges: { percentage: number; amount: number; usePercentage: boolean }[],
    calculateBruttoSumAndTaxValuesFromNettoSum: boolean,
    skontoPercentage: number | null = null,
    invoiceItems: LedgerImportInvoiceItem[] = [],
) {
    const arls = arlIds
        .map((bpId) => _getArlChildren(bpId, arlDict))
        .flat()
        .filter(arlIsAmountRelevant);

    return getLedgerImportSums(arls, allowanceCharges, calculateBruttoSumAndTaxValuesFromNettoSum, skontoPercentage, invoiceItems);
}
export interface arlTableData<F extends { [K in keyof F]: AbstractControl } = calculationArlForm> {
    arl: {
        Id: number;
        Multiplier: number;
        CurrencyCode?: string;
        Tax: number;
        ARLTemplateTypeId?: number; // only ARLTemplate
    };
    resource$: Observable<ResourceEntity | null>;
    material$: Observable<MaterialEntity | null>;
    job$: Observable<JobSpecificationEntity | null>;
    type$: Observable<AccountsReceivableLedgerTypeEnum | null>;
    // informationOptions$: Observable<{ value: string; optionLabel: string; resourceId: number }[]>;
    form: FormGroup<F>;
    getBaseCost: () => number;
    pieceCostAmountWithoutTax$: Observable<number>;
    costAmountWithoutTax$: Observable<number>;
    costAmountWithoutTaxAndPreChargeByType$: Observable<number>;
    disableCostAmount$: Observable<boolean>;
}

export const getLedgerImportCommissionChangeMutationPayload = (i: LedgerImportEntity, commissionId: number): DaveMutationChangeLedgerImportArgs => {
    return {
        id: i.Id,
        commissionId: commissionId,
        customerId: i.CustomerId || null,
        partnerOfficeId: i.PartnerOfficeId,
        consolidatedDate: i.ConsolidatedDate && FrontendDate(i.ConsolidatedDate),
        textTop: i.TextTop,
        textBottom: i.TextBottom,
        accountsReceivableLedgerIds: i.AccountsReceivableLedgerIds,
        consolidatedAmount: i.ConsolidatedAmount,
        consolidatedInvoiceId: i.ConsolidatedInvoiceId,
        customerName: i.CustomerName,
        customerStreet: i.CustomerStreet,
        customerCity: i.CustomerCity,
        customerPostalCode: i.CustomerPostalCode,
        customerCountry: i.CustomerCountry,
        customerEmail: i.CustomerEmail,
        deliveryName: i.DeliveryName,
        deliveryStreet: i.DeliveryStreet,
        deliveryPostalCode: i.DeliveryPostalCode,
        deliveryCity: i.DeliveryCity,
        partnerCountry: i.PartnerCountry,
        partnerEmail: i.PartnerEmail,
        partnerPhoneNumber: i.PartnerPhoneNumber,
        partnerName: i.PartnerName,
        partnerStreet: i.PartnerStreet,
        partnerCity: i.PartnerCity,
        partnerPostalCode: i.PartnerPostalCode,
        status: i.Status,
        bankname: i.Bankname,
        bic: i.Bic,
        blz: i.Blz,
        iban: i.Iban,
        ustId: i.UstId,
        userName: i.UserName,
        userEmail: i.UserEmail,
        consolidatedDeliveryDate: i.ConsolidatedDeliveryDate && FrontendDate(i.ConsolidatedDeliveryDate),
        dueDate: i.DueDate && FrontendDate(i.DueDate),
        discountPaymentDate: i.DiscountPaymentDate && FrontendDate(i.DiscountPaymentDate),
        discountPercentage: i.DiscountPercentage,
        guaranteedDiscountPercentage: i.GuaranteedDiscountPercentage,
        skontoIsAllowance: i.SkontoIsAllowance,
    };
};

export const getLedgerImportStatusChangeMutationPayload = (i: LedgerImportEntity, status: LedgerImportStatusEnum): DaveMutationChangeLedgerImportArgs => {
    return {
        id: i.Id,
        commissionId: i.CommissionId || null,
        customerId: i.CustomerId || null,
        partnerOfficeId: i.PartnerOfficeId,
        consolidatedDate: i.ConsolidatedDate && FrontendDate(i.ConsolidatedDate),
        textTop: i.TextTop,
        textBottom: i.TextBottom,
        accountsReceivableLedgerIds: i.AccountsReceivableLedgerIds,
        // consolidatedAmount: i.ConsolidatedAmount,
        consolidatedInvoiceId: i.ConsolidatedInvoiceId,
        customerName: i.CustomerName,
        customerStreet: i.CustomerStreet,
        customerCity: i.CustomerCity,
        customerPostalCode: i.CustomerPostalCode,
        customerCountry: i.CustomerCountry,
        customerEmail: i.CustomerEmail,
        deliveryName: i.DeliveryName,
        deliveryStreet: i.DeliveryStreet,
        deliveryPostalCode: i.DeliveryPostalCode,
        deliveryCity: i.DeliveryCity,
        partnerCountry: i.PartnerCountry,
        partnerEmail: i.PartnerEmail,
        partnerPhoneNumber: i.PartnerPhoneNumber,
        partnerName: i.PartnerName,
        partnerStreet: i.PartnerStreet,
        partnerCity: i.PartnerCity,
        partnerPostalCode: i.PartnerPostalCode,
        status: status !== null ? status : i.Status,
        bankname: i.Bankname,
        bic: i.Bic,
        blz: i.Blz,
        iban: i.Iban,
        ustId: i.UstId,
        userName: i.UserName,
        userEmail: i.UserEmail,
        consolidatedDeliveryDate: i.ConsolidatedDeliveryDate && FrontendDate(i.ConsolidatedDeliveryDate),
        dueDate: i.DueDate && FrontendDate(i.DueDate),
        discountPaymentDate: i.DiscountPaymentDate && FrontendDate(i.DiscountPaymentDate),
        discountPercentage: i.DiscountPercentage,
        guaranteedDiscountPercentage: i.GuaranteedDiscountPercentage,
        skontoIsAllowance: i.SkontoIsAllowance,
    };
};
export const baseCostFromFormValue = (
    formValue: Pick<FormGroup<calculationArlForm>['value'], 'IndividuallyCostCalculation' | 'BaseCost' | 'Type' | 'CostAmount' | 'CostAmountAllowance' | 'SurchargeOverride'>,
    zuschlaege?: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
    zuschlagInPercent?: number,
) => {
    if (isNotNullOrUndefined(formValue.SurchargeOverride)) {
        return financialMultiplication((formValue.CostAmount || 0), (1 + formValue.SurchargeOverride / 100))
    }
    if (formValue.IndividuallyCostCalculation) {
        return financialRound(formValue.BaseCost || 0);
    }
    if (!zuschlaege && zuschlagInPercent == null) {
        throw new Error('no zuschlaege or zuschlagInPercent');
    }
    const zuschlInPercent = zuschlagInPercent ?? (zuschlaege.find((z) => z.type === formValue.Type).form.value.percent || 0);
    return financialRound((formValue.CostAmount || 0) * (1 - (formValue.CostAmountAllowance || 0) / 100) * (1 + zuschlInPercent / 100));
};
export function getARLCostAmountWithAllowance(form: Pick<FormGroup<calculationArlForm>['value'], 'CostAmount' | 'CostAmountAllowance'> | Pick<FormGroup<calculationArlTemplateForm>['value'], 'CostAmount' | 'CostAmountAllowance'>) {
    const { CostAmount, CostAmountAllowance } = form;

    if (CostAmountAllowance && CostAmount) {
        return financialRound(CostAmount * (1 - CostAmountAllowance / 100));
    }
    return CostAmount;
}
export function getARLCostAmountWithAllowance$(form: FormGroup<calculationArlForm> | FormGroup<calculationArlTemplateForm>) {
    const costAmount$ = form.controls.CostAmount.valueChanges.pipe(startWith(null));
    const costAmountWithAllowance$ = combineLatest([costAmount$, form.controls.CostAmountAllowance.valueChanges.pipe(startWith(null))]).pipe(
        map(() => {
            return getARLCostAmountWithAllowance(form.getRawValue());
        }),
    );
    return costAmountWithAllowance$;
}
export function getARLPieceCostAmountWithoutTax(form: FormGroup<calculationArlForm>['value'] | FormGroup<calculationArlTemplateForm>['value'], preCharges?: LedgerImportARLCalculationPreChargeByType[]) {
    const { Type, IndividuallyCostCalculation } = form;
    const costAmountWithAllowance = getARLCostAmountWithAllowance(form);
    if (IndividuallyCostCalculation) {
        return costAmountWithAllowance;
    }
    if (preCharges) {
        const preChargeInPercent = preCharges.find((p) => p.arlType === Type)?.percentage || 0;
        return (costAmountWithAllowance || 0) + (costAmountWithAllowance || 0) * preChargeInPercent;
    }
    return costAmountWithAllowance;
}
export function getARLPieceCostAmountWithoutTax$(
    form: FormGroup<calculationArlForm> | FormGroup<calculationArlTemplateForm>,
    costAmountWithAllowance$: Observable<number>,
    aRLCalculationPreChargeByTypes$?: Observable<LedgerImportARLCalculationPreChargeByType[]>,
) {
    // const costAmount$ = form.controls.CostAmount.valueChanges.pipe(
    //     startWith(null),
    //     map(() => form.controls.CostAmount.value),
    // );
    // const costAmountWithAllowance$ = combineLatest([
    //     costAmount$,
    //     form.controls.CostAmountAllowance.valueChanges.pipe(
    //         startWith(null),
    //         map(() => form.controls.CostAmountAllowance.value),
    //     ),
    // ]).pipe(
    //     map(([costAmount, allowance])=> {
    //         if (allowance && costAmount) {
    //             return financialRound(costAmount*(1 - allowance / 100));
    //         }
    //         return costAmount;
    //     })
    // );
    return merge(form.controls.IndividuallyCostCalculation.valueChanges, form.controls.SurchargeOverride.valueChanges).pipe(
        startWith(null),
        switchMap(() => {
            if (isNotNullOrUndefined(form.controls.SurchargeOverride.getRawValue())) {
                return costAmountWithAllowance$;
                // return form.controls.BaseCost.valueChanges.pipe(startWith(null), map(() => form.controls.BaseCost.getRawValue()));
            } else {
                return aRLCalculationPreChargeByTypes$
                    ? combineLatest([
                          costAmountWithAllowance$,
                          form.controls.Type.valueChanges.pipe(
                              startWith(null),
                              map(() => form.controls.Type.getRawValue()),
                              filter(isNotNullOrUndefined),
                              distinctUntilChanged(),
                              switchMap((Type) => aRLCalculationPreChargeByTypes$.pipe(map((preCharges) => preCharges.find((p) => p.arlType === Type)?.percentage || 0))),
                              distinctUntilChanged(),
                          ),
                      ]).pipe(map(([CostAmount, preChargeInPercent]) => (CostAmount || 0) + (CostAmount || 0) * preChargeInPercent))
                    : costAmountWithAllowance$;
            }
        }),
    );
}

export function calculationArlGetBaseCostFromFormValue(form: FormGroup<calculationArlForm> | FormGroup<calculationArlTemplateForm>, zuschlaege: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[]) {
  return baseCostFromFormValue(
      {
          IndividuallyCostCalculation: form.controls.IndividuallyCostCalculation.getRawValue(),
          BaseCost: form.controls.BaseCost.getRawValue(),
          Type: form.controls.Type.getRawValue(),
          CostAmount: form.controls.CostAmount.getRawValue(),
          CostAmountAllowance: form.controls.CostAmountAllowance.getRawValue(),
      },
      zuschlaege,
  );
}

export function getARLTableData(
    arl: AccountsReceivableLedgerTemplateEntity | null,
    form: FormGroup<calculationArlTemplateForm>,
    store: Store<State>,
    zuschlaege: any,
    getMaterial$: (id: number) => Observable<MaterialEntity>,
): arlTableData<calculationArlTemplateForm>;
export function getARLTableData(
    arl: AccountsReceivableLedgerEntity | null,
    form: FormGroup<calculationArlForm>,
    store: Store<State>,
    zuschlaege: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
    getMaterial$: (id: number) => Observable<MaterialEntity>,
    aRLCalculationPreChargeByTypes$: Observable<LedgerImportARLCalculationPreChargeByType[]>,
): arlTableData<calculationArlForm>;

export function getARLTableData(
    arl: AccountsReceivableLedgerEntity | AccountsReceivableLedgerTemplateEntity | null,
    form: FormGroup<calculationArlForm> | FormGroup<calculationArlTemplateForm>,
    store: Store<State>,
    zuschlaege: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
    getMaterial$: (id: number) => Observable<MaterialEntity>,
    aRLCalculationPreChargeByTypes$?: Observable<LedgerImportARLCalculationPreChargeByType[]>,
): arlTableData<calculationArlForm> | arlTableData<calculationArlTemplateForm> {
    const baseCost = () => {
        return calculationArlGetBaseCostFromFormValue(
            form,
            zuschlaege,
        );
    };
    // const costAmount$ = form.controls.CostAmount.valueChanges.pipe(
    //     startWith(null),
    //     map(() => form.controls.CostAmount.value),
    // );
    const costAmountWithAllowance$ = getARLCostAmountWithAllowance$(form);
    // const costAmountWithAllowance$ = combineLatest([
    //     costAmount$,
    //     form.controls.CostAmountAllowance.valueChanges.pipe(
    //         startWith(null),
    //         map(() => form.controls.CostAmountAllowance.value),
    //     ),
    // ]).pipe(
    //     map(([costAmount, allowance])=> {
    //         if (allowance && costAmount) {
    //             return financialRound(costAmount*(1 - allowance / 100));
    //         }
    //         return costAmount;
    //     })
    // );
    const quantity$ = form.controls.Quantity.valueChanges.pipe(
        startWith(null),
        map(() => form.controls.Quantity.value),
    );
    const pieceCostAmountWithoutTax$ = getARLPieceCostAmountWithoutTax$(form, costAmountWithAllowance$, aRLCalculationPreChargeByTypes$);
    // const pieceCostAmountWithoutTax$ = form.controls.IndividuallyCostCalculation.valueChanges.pipe(
    //     startWith(null),
    //     switchMap(() => {
    //         if (form.controls.IndividuallyCostCalculation.getRawValue()) {
    //             return costAmountWithAllowance$;
    //             // return form.controls.BaseCost.valueChanges.pipe(startWith(null), map(() => form.controls.BaseCost.getRawValue()));
    //         } else {
    //             return aRLCalculationPreChargeByTypes$
    //                 ? combineLatest([
    //                     costAmountWithAllowance$,
    //                       form.controls.Type.valueChanges.pipe(
    //                           startWith(null),
    //                           map(() => form.controls.Type.getRawValue()),
    //                           filter(isNotNullOrUndefined),
    //                           distinctUntilChanged(),
    //                           switchMap((Type) => aRLCalculationPreChargeByTypes$.pipe(map((preCharges) => preCharges.find((p) => p.arlType === Type)?.percentage || 0))),
    //                           distinctUntilChanged(),
    //                       ),
    //                   ]).pipe(map(([CostAmount, preChargeInPercent]) => (CostAmount || 0) + (CostAmount || 0) * preChargeInPercent))
    //                 : costAmountWithAllowance$;
    //         }
    //     }),
    // );
    const costAmountWithoutTaxAndPreChargeByType$ = combineLatest([quantity$, costAmountWithAllowance$]).pipe(map(([Quantity, CostAmount]) => (Quantity || 0) * (CostAmount || 0)));
    const disableCostAmount$ = merge(form.controls.ResourceId.valueChanges, form.controls.MaterialId.valueChanges, form.controls.JobSpecificationId.valueChanges).pipe(
        startWith(null),
        map(() => {
            console.log(form.value);
            return !!form.controls.JobSpecificationId.value || !!form.controls.ResourceId.value || !!form.controls.MaterialId.value;
        }),
        tap((v) => console.log('disabled: ' + v)),
    );
    return {
        arl,
        resource$: form.controls.ResourceId.valueChanges.pipe(
            startWith(null),
            switchMap(() => (form.controls.ResourceId.getRawValue() ? store.select(getResourceById({ id: form.controls.ResourceId.getRawValue() })) : of(null))),
        ),
        material$: form.controls.MaterialId.valueChanges.pipe(
            startWith(null),
            switchMap(() => (form.controls.MaterialId.getRawValue() ? getMaterial$(form.controls.MaterialId.getRawValue()) : of(null))),
        ),
        job$: form.controls.JobSpecificationId.valueChanges.pipe(
            startWith(null),
            switchMap(() => (form.controls.JobSpecificationId.getRawValue() ? store.select(getJobSpecificationById({ id: form.controls.JobSpecificationId.getRawValue() })) : of(null))),
        ),
        type$ :form.controls.Type.valueChanges.pipe(
            startWith(null),
            map(() => form.controls.Type.getRawValue()),
            shareReplay({refCount: true, bufferSize: 1})
        ),
        disableCostAmount$,
        getBaseCost: () => baseCost(),
        form,

        pieceCostAmountWithoutTax$,
        costAmountWithoutTax$: combineLatest([pieceCostAmountWithoutTax$, quantity$]).pipe(map(([pieceCostAmountWithoutTax, Quantity]) => (pieceCostAmountWithoutTax || 0) * (Quantity || 0))),
        costAmountWithoutTaxAndPreChargeByType$,
    } as arlTableData<calculationArlForm> | arlTableData<calculationArlTemplateForm>;
}
export function getAllowanceChargeAmount(before: number, percentage: number) {
    return financialRound(before * percentage);
}
export function getAllowanceChargePercent(before: number, amount: number) {
    return before
        ? Big(amount / before)
              .round(4, Big.roundHalfUp)
              .toNumber()
        : 0;
}
export function addAllowanceCharges(before: number, allowanceCharges: { percentage: number; amount: number; usePercentage: boolean }[], skontoPercentage: number | null = null) {
    allowanceCharges.forEach((ac) => {
        if (ac.usePercentage) {
            before = before + getAllowanceChargeAmount(before, ac.percentage);
        } else {
            before = before + ac.amount;
        }
        console.log(ac, before);
    });
    if (skontoPercentage) {
        before = before - getAllowanceChargeAmount(before, skontoPercentage / 100);
    }
    return before;
}
export const arlIsAmountRelevant = (arl: Pick<AccountsReceivableLedgerEntity, 'IsOptional' | 'CrossedOut'>) => !arl.IsOptional && !arl.CrossedOut; /* ggf zun rausfiltern von kalkulierten -> ? && arl.IsVisible*/

export function getLedgerImportSums(
    filteredArls: Pick<AccountsReceivableLedgerEntity, 'Quantity' | 'BaseCost' | 'BaseCostGross' | 'Tax' | 'Type' | 'Id' | 'BaseCostAllowances' | 'ParentId' | 'SurchargeOverride' | 'FixedPrices'>[],
    allowanceCharges: { percentage: number; amount: number; usePercentage: boolean }[],
    calculateBruttoSumAndTaxValuesFromNettoSum: boolean,
    skontoPercentage: number | null = null,
    invoiceItems: LedgerImportInvoiceItem[] = [],
) {
    console.group('getLedgerImportSums');
    // console.log(filteredArls);
    const _getParentBaseCostAllowances = (id: number, arls: typeof _filteredArlsMap) => {
        const parents: AccountsReceivableLedgerEntity['BaseCostAllowances'][] = [];
        let pid = arls.get(id).ParentId;
        while (pid) {
            const p = arls.get(pid);
            if (!p) {
                console.error('parent not found', pid);
            }
            parents.push(p.BaseCostAllowances);
            pid = p.ParentId;
        }
        return parents;
    };
    const _filteredArlsMap = new Map(filteredArls.map((a) => [a.Id, a]));
    const getParentBaseCostAllowances = (id: number) => {
        return _getParentBaseCostAllowances(id, _filteredArlsMap);
    };
    // console.time('getArlSums')

    const arlAmounts = getArlSums(filteredArls, allowanceCharges, /*skontoPercentage,*/ getParentBaseCostAllowances);
    // console.timeEnd('getArlSums')

    // const arlAmountsFromLedgerImport = getArlSums(arlsFromLedgerImport, [], /*null*/() => []);

    // console.log(arlAmounts);
    // const y = [];
    // arlAmounts.forEach((v,k) => y.push({id: k, ...v}))
    // console.log(y);
    // console.warn('########');
    const withoutAllowanceCharges = {
        nettoSum: 0,
        bruttoSum: 0,
        taxSum: 0,
    };
    const withAllowanceCharges = {
        nettoSum: 0,
        bruttoSum: 0,
        taxSum: 0,
        taxSumsByType: new Map<number, number>([]),
    };
    let withAllowanceChargesButScontoIgnored: {
        nettoSum: number,
        bruttoSum: number,
        taxSum: number,
        // taxSumsByType: new Map<number, number>([]),
    };

    arlAmounts.forEach((sum) => {
        withoutAllowanceCharges.nettoSum = financialRound(withoutAllowanceCharges.nettoSum + sum.withoutAllowanceCharges.nettoAmount);
        withoutAllowanceCharges.bruttoSum = financialRound(withoutAllowanceCharges.bruttoSum + sum.withoutAllowanceCharges.bruttoAmount);
        withoutAllowanceCharges.taxSum = financialRound(withoutAllowanceCharges.taxSum + sum.withoutAllowanceCharges.taxAmount);

        // withAllowanceCharges.nettoSum = financialRound(withAllowanceCharges.nettoSum + sum.withAllowanceCharges.nettoAmount);
        withAllowanceCharges.bruttoSum = financialRound(withAllowanceCharges.bruttoSum + sum.withAllowanceCharges.bruttoAmount);
        withAllowanceCharges.taxSum = financialRound(withAllowanceCharges.taxSum + sum.withAllowanceCharges.taxAmount);
        withAllowanceCharges.taxSumsByType.set(sum.withAllowanceCharges.taxType, financialRound((withAllowanceCharges.taxSumsByType.get(sum.withAllowanceCharges.taxType) || 0) + sum.withAllowanceCharges.taxAmount));
    });

    console.log('before allowanceCharges', JSON.parse(JSON.stringify({ withoutAllowanceCharges, withAllowanceCharges })));
    /**
     Calc with allowance direkt from without allowance sum for hiding gounding issue sums direct from
     */
    withAllowanceCharges.nettoSum = addAllowanceCharges(withoutAllowanceCharges.nettoSum, allowanceCharges);
    console.log('with allowanceCharges', JSON.parse(JSON.stringify({ withoutAllowanceCharges, withAllowanceCharges })));

    if (calculateBruttoSumAndTaxValuesFromNettoSum) {
        // todo überdenken tut so mit den neuen Brutto preisen auf jeden Fall nicht
        // if (false && withAllowanceCharges.taxSumsByType.size === 1) {
        //     // alert('+')
        //     let taxPercent = 0;
        //     withAllowanceCharges.taxSumsByType.forEach((sum, tax) => {
        //         taxPercent = tax;
        //     });
        //     withAllowanceCharges.taxSumsByType.set(taxPercent, financialRound((withAllowanceCharges.nettoSum * taxPercent) / 100));
        //     withAllowanceCharges.taxSum = withAllowanceCharges.taxSumsByType.get(taxPercent);
        // }

        // withAllowanceCharges.bruttoSum = addAllowanceCharges(withoutAllowanceCharges.bruttoSum, allowanceCharges );
        function getTaxSum() {
            let taxSum = 0;
            withAllowanceCharges.taxSumsByType.forEach((sum, tax) => {
                taxSum += sum;
            });
            return taxSum;
        }
        const taxSum = getTaxSum();
        if (taxSum > 0) {
            withAllowanceCharges.taxSumsByType.forEach((sum, tax) => {
                // alert('tax: '+tax)
                const taxAnteil = sum / taxSum;
                withAllowanceCharges.taxSumsByType.set(tax, financialRound((withAllowanceCharges.nettoSum * taxAnteil * tax) / 100));
            });
            withAllowanceCharges.taxSum = financialRound(getTaxSum());
        }
        console.log('with round fix', JSON.parse(JSON.stringify({ withAllowanceCharges })));

        withAllowanceCharges.bruttoSum = financialRound(withAllowanceCharges.taxSum + withAllowanceCharges.nettoSum);

        console.log('with brutto sum', JSON.parse(JSON.stringify({ withAllowanceCharges })));
    }

    /**
     * clone before sconto calculation
     */
    withAllowanceChargesButScontoIgnored = {
        nettoSum: withAllowanceCharges.nettoSum,
        bruttoSum: withAllowanceCharges.bruttoSum,
        taxSum: withAllowanceCharges.taxSum,
    };
    if (skontoPercentage) {
        withAllowanceCharges.bruttoSum = financialRound(withAllowanceCharges.bruttoSum - getAllowanceChargeAmount(withAllowanceCharges.bruttoSum, skontoPercentage / 100));
        withAllowanceCharges.nettoSum = financialRound(withAllowanceCharges.nettoSum - getAllowanceChargeAmount(withAllowanceCharges.nettoSum, skontoPercentage / 100));
        withAllowanceCharges.taxSum = financialRound(withAllowanceCharges.bruttoSum - withAllowanceCharges.nettoSum);
    }
    console.log('with skontoPercentage', JSON.parse(JSON.stringify({ withAllowanceCharges })));

    invoiceItems.forEach((sum) => {
        withoutAllowanceCharges.nettoSum = financialRound(withoutAllowanceCharges.nettoSum - (sum.amountNetto || 0));
        withoutAllowanceCharges.bruttoSum = financialRound(withoutAllowanceCharges.bruttoSum - (sum.amountBrutto || 0));
        withoutAllowanceCharges.taxSum = financialRound(withoutAllowanceCharges.taxSum - ((sum.amountBrutto || 0) - (sum.amountNetto || 0)));

        withAllowanceCharges.nettoSum = financialRound(withAllowanceCharges.nettoSum - (sum.amountNetto || 0));
        withAllowanceCharges.bruttoSum = financialRound(withAllowanceCharges.bruttoSum - (sum.amountBrutto || 0));
        withAllowanceCharges.taxSum = financialRound(withAllowanceCharges.taxSum - ((sum.amountBrutto || 0) - (sum.amountNetto || 0)));

        withAllowanceChargesButScontoIgnored.nettoSum = financialRound(withAllowanceChargesButScontoIgnored.nettoSum - (sum.amountNetto || 0));
        withAllowanceChargesButScontoIgnored.bruttoSum = financialRound(withAllowanceChargesButScontoIgnored.bruttoSum - (sum.amountBrutto || 0));
        withAllowanceChargesButScontoIgnored.taxSum = financialRound(withAllowanceChargesButScontoIgnored.taxSum - ((sum.amountBrutto || 0) - (sum.amountNetto || 0)));

        // withAllowanceCharges.nettoSum = roundToTwo(withAllowanceCharges.nettoSum + sum.withAllowanceCharges.nettoAmount);
        // withAllowanceCharges.bruttoSum = roundToTwo(withAllowanceCharges.bruttoSum + sum.withAllowanceCharges.bruttoAmount);
        // const taxAmount = ((sum.amountBrutto || 0) - (sum.amountNetto || 0));
        // const taxType = financialRound(((sum.amountBrutto / sum.amountNetto) - 1) * 100, 1)
        // withAllowanceCharges.taxSum = roundToTwo(withAllowanceCharges.taxSum - taxAmount);
        // withAllowanceCharges.nettoSum = roundToTwo(withAllowanceCharges.nettoSum - taxAmount);
        // // unnötig
        // withAllowanceCharges.taxSumsByType.set(taxType, financialRound((withAllowanceCharges.taxSumsByType.get(taxType) || 0) - taxAmount))
    });
    console.log('with invoiceItems', JSON.parse(JSON.stringify({ withoutAllowanceCharges, withAllowanceCharges })));
    // withAllowanceCharges.bruttoSum = financialRound(withAllowanceCharges.nettoSum + withAllowanceCharges.taxSum);
    // console.log('with brutto sum', JSON.parse(JSON.stringify({ withAllowanceCharges })))

    console.groupEnd();

    return {
        withAllowanceCharges: withAllowanceCharges,
        withoutAllowanceCharges: withoutAllowanceCharges,
        withAllowanceChargesButScontoIgnored: withAllowanceChargesButScontoIgnored,
    };
}
export function getArlSums(
    filteredArls: Pick<AccountsReceivableLedgerEntity, 'Quantity' | 'BaseCost' | 'BaseCostGross' | 'Tax' | 'Type' | 'Id' | 'BaseCostAllowances' | 'SurchargeOverride' | 'FixedPrices'>[],
    allowanceCharges: { percentage: number; amount: number; usePercentage: boolean }[],
    baseCostAllowancesFromParents: (id: number) => Array<AccountsReceivableLedgerEntity['BaseCostAllowances']>,
    // skontoPercentage: number | null = null,
) {
    let nettoSumWithoutDiscount = 0;
    const baseCostAllowancesFromParentsMap = new Map(filteredArls.map((a) => [a.Id, baseCostAllowancesFromParents(a.Id)]));

    filteredArls.forEach((bp) => {
        nettoSumWithoutDiscount = financialRound(nettoSumWithoutDiscount + CalcAccountsReceivableLedgerNettoAmountWithAllowances(bp, baseCostAllowancesFromParentsMap.get(bp.Id)));
    });
    const nettoSumWithDiscount = addAllowanceCharges(nettoSumWithoutDiscount, allowanceCharges);
    // console.warn('############# get arl sums ##########');
    // console.log('nettoSumWithDiscount', nettoSumWithDiscount);
    const discountPercentage = nettoSumWithoutDiscount ? 1 - nettoSumWithDiscount / nettoSumWithoutDiscount : 0;

    //console.time('resultMap')
    const resultMap = new Map<
        number,
        { withAllowanceCharges: { taxAmount: number; nettoAmount: number; bruttoAmount: number; taxType: number }; withoutAllowanceCharges: { taxAmount: number; nettoAmount: number; bruttoAmount: number; taxType: number } }
    >(
        filteredArls.map((bp) => {
            const nettoAmountWithout = CalcAccountsReceivableLedgerNettoAmountWithAllowances(bp, baseCostAllowancesFromParentsMap.get(bp.Id));
            const nettoAmountWith = financialRound(CalcDiscount(nettoAmountWithout, discountPercentage * 100));
            // nicht ändern, wird im BE auch so gerechnet

            // todo dorty fix für kaputte brutto preise
            const baseCostGrossFix = financialRound(bp.BaseCost * ((bp.Tax || 0) / 100 + 1));
            let correctedBaseCostGross = bp.BaseCostGross;

            if (baseCostGrossFix - bp.BaseCostGross >= 1 || baseCostGrossFix - bp.BaseCostGross <= -1) {
                correctedBaseCostGross = baseCostGrossFix;

                const error = new Error('Bruttopreis an Position falsch');
                Sentry.captureException(error, {
                    level: 'error',
                    contexts: {
                        auth: { Bearer: localStorage.getItem('token') },
                    },
                    extra: {
                        arlValues: bp,
                        baseCostGrossFix,
                    },
                });
            }

            const bruttoAmountWithOut = CalcAccountsReceivableLedgerBruttoAmountWithAllowances({ ...bp, BaseCostGross: correctedBaseCostGross }, baseCostAllowancesFromParentsMap.get(bp.Id));
            const bruttoAmountWith = financialRound(CalcDiscount(bruttoAmountWithOut, discountPercentage * 100));

            return [
                bp.Id,
                {
                    withoutAllowanceCharges: {
                        bruttoAmount: bruttoAmountWithOut,
                        nettoAmount: nettoAmountWithout,
                        taxAmount: financialRound(bruttoAmountWithOut - nettoAmountWithout),
                        taxType: bp.Tax,
                    },
                    withAllowanceCharges: {
                        bruttoAmount: bruttoAmountWith,
                        nettoAmount: nettoAmountWith,
                        taxAmount: financialRound(bruttoAmountWith - nettoAmountWith),
                        taxType: bp.Tax,
                    },
                },
            ];
        }),
    );
    //console.timeEnd('resultMap')
    return resultMap;
}
export function roundToTwo(num: number) {
    return financialRound(num);
}
export const emptyAllowanceChargeForm = () =>
    new FormGroup({
        percentage: new FormControl<number>(null),
        amount: new FormControl<number>(null),
        // reasonCode
        customReason: new FormControl<string>(null, Validators.required),
        sum: new FormControl<number>(null),
        usePercentage: new FormControl<boolean>(false),
    });
export const getCostFromArlTemplate = async (arl: AccountsReceivableLedgerTemplateEntity, store: Store<State>, materialDataService: MaterialDataService) => {
    let cost: number = null;
    switch (arl.Type) {
        case AccountsReceivableLedgerTypeEnum.EmployeeArl:
            if (arl.JobSpecificationId) {
                const job = await firstValueFrom(getFetched$(store, getJobSpecificationFetched, getJobSpecificationById({ id: arl.JobSpecificationId })));
                cost = job.Amount;
            }
            break;
        case AccountsReceivableLedgerTypeEnum.ResourceArl:
            if (arl.ResourceId) {
                const resource = await firstValueFrom(getFetched$(store, getResourcesFetched, getResourceById({ id: arl.ResourceId })));
                cost = (resource.MaxChargeRate || 0) / 100;
            }
            break;
        case AccountsReceivableLedgerTypeEnum.MaterialArl:
            if (arl.MaterialId) {
                const material = await firstValueFrom(materialDataService.getMaterialById$(arl.MaterialId));
                cost = (material?.Cost || 0) / 100;
            }
            break;
    }
    return cost;
};
export function switchHourMinuteCalc(targetQuantityType: number, form: calculationArlFormGroup, minuteTypeId: number, hourTypeId: number) {
    if (targetQuantityType === minuteTypeId && form.value.QuantityTypeId === hourTypeId) {
        form.patchValue({
            QuantityTypeId: minuteTypeId,
            CostAmount: financialRound((form.value.CostAmount || 0) / 60),
            Quantity: (form.value.Quantity || 0) * 60,
        });
        form.controls.QuantityTypeId.markAsDirty();
        form.controls.CostAmount.markAsDirty();
        form.controls.Quantity.markAsDirty();
        if (form.value.IndividuallyCostCalculation) {
            form.controls.BaseCost.patchValue(financialRound((form.value.BaseCost || 0) / 60));
            form.controls.BaseCost.markAsDirty();
        }
    } else if (targetQuantityType === hourTypeId && form.value.QuantityTypeId === minuteTypeId) {
        form.patchValue({
            QuantityTypeId: hourTypeId,
            CostAmount: financialRound((form.value.CostAmount || 0) * 60),
            Quantity: (form.value.Quantity || 0) / 60,
        });
        form.controls.QuantityTypeId.markAsDirty();
        form.controls.CostAmount.markAsDirty();
        form.controls.Quantity.markAsDirty();
        if (form.value.IndividuallyCostCalculation) {
            form.controls.BaseCost.patchValue(financialRound((form.value.BaseCost || 0) * 60));
            form.controls.BaseCost.markAsDirty();
        }
    }
}
export function switchHourMinute(qType: number, form: arlTableDataArlForm, minuteTypeId: number, hourTypeId: number) {
    console.log({ qType, 'form.value.QuantityTypeId': form.value.QuantityTypeId, form, minuteTypeId, hourTypeId });
    if (qType === minuteTypeId && form.value.QuantityTypeId === hourTypeId) {
        const BaseCost = financialRound((form.value.BaseCost || 0) / 60);
        const SalaryBaseCost = financialRound((form.value.SalaryBaseCost || 0) / 60);
        form.patchValue({
            QuantityTypeId: minuteTypeId,
            BaseCost,
            CostAmount: financialRound((form.value.CostAmount || 0) / 60),
            SalaryBaseCost,
            sonstigesBaseCost: financialRound(BaseCost - SalaryBaseCost),
            Quantity: (form.value.Quantity || 0) * 60,
        });
        form.controls.QuantityTypeId.markAsDirty();
        form.controls.BaseCost.markAsDirty();
        form.controls.CostAmount.markAsDirty();
        form.controls.SalaryBaseCost.markAsDirty();
        form.controls.sonstigesBaseCost.markAsDirty();
        form.controls.Quantity.markAsDirty();
    } else if (qType === hourTypeId && form.value.QuantityTypeId === minuteTypeId) {
        const BaseCost = financialRound((form.value.BaseCost || 0) * 60);
        const SalaryBaseCost = financialRound((form.value.SalaryBaseCost || 0) * 60);
        form.patchValue({
            QuantityTypeId: hourTypeId,
            BaseCost,
            CostAmount: financialRound((form.value.CostAmount || 0) * 60),
            SalaryBaseCost,
            sonstigesBaseCost: financialRound(BaseCost - SalaryBaseCost),
            Quantity: (form.value.Quantity || 0) / 60,
        });
        form.controls.QuantityTypeId.markAsDirty();
        form.controls.BaseCost.markAsDirty();
        form.controls.CostAmount.markAsDirty();
        form.controls.SalaryBaseCost.markAsDirty();
        form.controls.sonstigesBaseCost.markAsDirty();
        form.controls.Quantity.markAsDirty();
    }
}
export const ARL_DEFAULT_VALUES: Pick<AccountsReceivableLedgerPostType, 'multiplikator' | 'isVisible' | 'itemNumber'> = {
    multiplikator: 1,
    isVisible: true,
    itemNumber: AccountsReceivableLedgerEntity.autogenerateItemNumber,
};
/**
 * @param ParentBaseCostAllowances must be in the correct order from leaf to root
 */
export const CalcAccountsReceivableLedgerNettoBaseCostWithOnlyCharges = (
    { Type, BaseCost, BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'Type' | 'BaseCost' | 'SurchargeOverride' | 'FixedPrices'> & { BaseCostAllowances: Partial<AccountsReceivableLedgerEntity['BaseCostAllowances']> },
    ParentBaseCostAllowances: Array<AccountsReceivableLedgerEntity['BaseCostAllowances']>,
) => {
    return CalcAccountsReceivableLedgerNettoBaseCostWithAllowancesCharges({ Type, BaseCost, BaseCostAllowances, SurchargeOverride, FixedPrices }, ParentBaseCostAllowances, true);
};

/**
 * @param ParentBaseCostAllowances must be in the correct order from leaf to root
 */
export const CalcAccountsReceivableLedgerBruttoBaseCostWithOnlyCharges = (
    { Type, BaseCostGross, BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'Type' | 'BaseCostGross' | 'SurchargeOverride' | 'FixedPrices'> & { BaseCostAllowances: Partial<AccountsReceivableLedgerEntity['BaseCostAllowances']> },
    ParentBaseCostAllowances: Array<AccountsReceivableLedgerEntity['BaseCostAllowances']>,
) => {
    return CalcAccountsReceivableLedgerBruttoBaseCostWithAllowancesCharges({ Type, BaseCostGross, BaseCostAllowances, SurchargeOverride, FixedPrices }, ParentBaseCostAllowances, true);
};

/**
 * @param ParentBaseCostAllowances must be in the correct order from leaf to root
 */
export const CalcAccountsReceivableLedgerNettoBaseCostWithAllowancesCharges = (
    { Type, BaseCost, BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'Type' | 'BaseCost' | 'SurchargeOverride' | 'FixedPrices'> & { BaseCostAllowances: Partial<AccountsReceivableLedgerEntity['BaseCostAllowances']> },
    ParentBaseCostAllowances: Array<AccountsReceivableLedgerEntity['BaseCostAllowances']>,
    skipAllowances = false,
) => {
    return CalcAccountsReceivableLedgerNettoAmountWithAllowances({ Type, BaseCost, BaseCostAllowances, Quantity: 1, SurchargeOverride, FixedPrices }, ParentBaseCostAllowances, skipAllowances);
};
/**
 * @param ParentBaseCostAllowances must be in the correct order from leaf to root
 */
export const CalcAccountsReceivableLedgerBruttoBaseCostWithAllowancesCharges = (
    { Type, BaseCostGross, BaseCostAllowances, SurchargeOverride, FixedPrices }: Pick<AccountsReceivableLedgerEntity, 'Type' | 'BaseCostGross' | 'SurchargeOverride' | 'FixedPrices'> & { BaseCostAllowances: Partial<AccountsReceivableLedgerEntity['BaseCostAllowances']> },
    ParentBaseCostAllowances: Array<AccountsReceivableLedgerEntity['BaseCostAllowances']>,
    skipAllowances = false,
) => {
    return CalcAccountsReceivableLedgerBruttoAmountWithAllowances({ Type, BaseCostGross, BaseCostAllowances, Quantity: 1, SurchargeOverride, FixedPrices }, ParentBaseCostAllowances, skipAllowances);
};
export function arlFormUpdateQuantityByQuantityHistory(form: { controls: Pick<arlTableDataArlForm['controls'], 'Quantity' | 'UseQuantityHistory' | 'QuantityHistory'> }) {
    if (!form.controls.UseQuantityHistory.getRawValue()) {
        return;
    }
    let quantity = 0;
    form.controls.QuantityHistory.value.forEach((q) => {
        quantity += q.quantityDelta;
    });
    if (quantity !== form.controls.Quantity.value) {
        form.controls.Quantity.setValue(financialRound(quantity, QUANTITY_MAX_DECIMAL_PLACES));
        form.controls.Quantity.markAsDirty();
    }
}


export function getParentsBaseCostAllowanceCharges(id: number, sourceDictionary: Dictionary<AccountsReceivableLedgerEntity>, includingSelf = false): Array<AccountsReceivableLedgerEntity['BaseCostAllowances']> {
    const parentBca = getParents(id, sourceDictionary).map(p => p.BaseCostAllowances);
    return includingSelf ? [sourceDictionary[id].BaseCostAllowances, ...parentBca] : parentBca;
}


export type AllowanceChargesForm = FormGroup<{
    percentage: FormControl<number>;
    amount: FormControl<number>;
    customReason: FormControl<string>;
    sum: FormControl<number>;
    usePercentage: FormControl<boolean>;
}>;
export function setAllowanceChargesFormSums(basePrice: number, allowanceChargesForm: FormArray<AllowanceChargesForm>) {
    let base = basePrice;
    allowanceChargesForm?.controls.forEach((c, i) => {
        console.warn('### --- ###');
        console.log('### --- ###', c, c.value, base);
        if (c.value.usePercentage) {
            const amount = getAllowanceChargeAmount(base, c.value.percentage);
            c.patchValue(
                {
                    sum: base + amount,
                    amount,
                },
                { emitEvent: false },
            );
        } else {
            const percent = getAllowanceChargePercent(base, c.value.amount);
            c.patchValue(
                {
                    sum: base + c.value.amount,
                    percentage: percent,
                },
                { emitEvent: false },
            );
        }
        base = c.value.sum;
    });
}
